// <s-background-video> keeps a muted, looping decorative video under user control:
// a --toggle command pauses/resumes it, and reduced-motion preferences stop it entirely.
// With data-crossfade="<seconds>", the loop seam becomes a crossfade: a clone of the
// video starts from the beginning and fades in over the outgoing copy's final seconds.
class SBackgroundVideo extends HTMLElement {
  #abort;
  #videos = [];
  #active = 0;
  #fading = false;
  #fadeTimer;
  #button;
  #motionQuery;

  get #video() {
    return this.#videos[this.#active];
  }

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const video = this.querySelector('video');
    this.#button = this.querySelector('[command="--toggle"]');
    if (!video) return;

    this.#videos = [video];
    this.#setupCrossfade(signal);

    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#applyMotionPreference();
    this.#motionQuery.addEventListener('change', () => this.#applyMotionPreference(), { signal });

    this.addEventListener('command', (event) => {
      if (event.command === '--toggle') this.#toggle();
    }, { signal });

    for (const v of this.#videos) {
      v.addEventListener('play', () => this.#sync(), { signal });
      v.addEventListener('pause', () => this.#sync(), { signal });
    }
    this.#sync();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    clearTimeout(this.#fadeTimer);
    this.#videos.slice(1).forEach((v) => v.remove());
    this.#videos = [];
  }

  #setupCrossfade(signal) {
    const seconds = parseFloat(this.dataset.crossfade);
    if (!(seconds > 0)) return;

    const primary = this.#videos[0];
    const clone = primary.cloneNode(true);
    clone.removeAttribute('autoplay');
    clone.preload = 'auto';
    primary.after(clone);
    this.#videos.push(clone);

    this.#videos.forEach((v, i) => {
      v.loop = false;
      v.style.transition = `opacity ${seconds}s linear`;
      v.style.opacity = i === 0 ? '1' : '0';
      v.style.zIndex = i === 0 ? '-9' : '-10';
      v.addEventListener('timeupdate', () => {
        if (v !== this.#video || this.#fading || !v.duration) return;
        if (v.currentTime >= v.duration - seconds) this.#crossfade(seconds);
      }, { signal });
      // Safety net if timeupdate never landed inside the fade window.
      v.addEventListener('ended', () => {
        if (v === this.#video && !this.#fading) this.#crossfade(0);
      }, { signal });
    });
  }

  #crossfade(seconds) {
    const outgoing = this.#video;
    const nextIndex = (this.#active + 1) % this.#videos.length;
    const incoming = this.#videos[nextIndex];
    this.#fading = true;

    // Incoming stacks above and fades in; outgoing stays opaque beneath so the photo never shows through.
    incoming.currentTime = 0;
    incoming.style.zIndex = '-9';
    outgoing.style.zIndex = '-10';
    this.#active = nextIndex;
    incoming.play().catch(() => {});
    requestAnimationFrame(() => { incoming.style.opacity = '1'; });

    this.#fadeTimer = setTimeout(() => {
      outgoing.pause();
      outgoing.style.transition = 'none';
      outgoing.style.opacity = '0';
      outgoing.currentTime = 0;
      requestAnimationFrame(() => {
        outgoing.style.transition = incoming.style.transition;
        this.#fading = false;
      });
    }, seconds * 1000);
  }

  #applyMotionPreference() {
    if (this.#motionQuery.matches) {
      this.#videos.forEach((v) => v.pause());
    } else if (this.dataset.userPaused !== 'true') {
      this.#video.play().catch(() => {});
    }
  }

  #toggle() {
    if (this.#video.paused) {
      delete this.dataset.userPaused;
      this.#videos.forEach((v) => { if (v === this.#video || this.#fading) v.play().catch(() => {}); });
    } else {
      // Explicit pause is sticky: nothing else restarts the video.
      this.dataset.userPaused = 'true';
      this.#videos.forEach((v) => v.pause());
    }
  }

  #sync() {
    if (!this.#button) return;
    const paused = this.#video.paused;
    this.#button.setAttribute('aria-pressed', String(paused));
    this.#button.setAttribute('aria-label', paused ? this.dataset.playLabel : this.dataset.pauseLabel);
    this.toggleAttribute('data-paused', paused);
  }
}

if (!customElements.get('s-background-video')) {
  customElements.define('s-background-video', SBackgroundVideo);
}
